import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { initializePaystackPayment, verifyPaystackPayment } from '../../services/paystackService';

const PaystackPaymentForm = ({ plan, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isPaystackLoaded, setIsPaystackLoaded] = useState(false);
  const [userDetails, setUserDetails] = useState({
    email: '',
    firstName: '',
    lastName: '',
    phone: ''
  });

  const { user, token } = useSelector((state) => state.auth);

  // Load Paystack script with better error handling
  useEffect(() => {
    const loadPaystackScript = () => {
      // Check if already loaded
      if (window.PaystackPop) {
        setIsPaystackLoaded(true);
        return;
      }

      // Remove any existing Paystack scripts to avoid conflicts
      const existingScripts = document.querySelectorAll('script[src*="paystack"]');
      existingScripts.forEach(script => script.remove());

      const script = document.createElement('script');
      script.src = 'https://js.paystack.co/v1/inline.js';
      script.async = true;
      script.onload = () => {
        console.log('Paystack script loaded successfully');
        setIsPaystackLoaded(true);
      };
      script.onerror = (error) => {
        console.error('Failed to load Paystack script:', error);
        setError('Failed to load Paystack. Please refresh the page and try again.');
      };
      document.head.appendChild(script);
    };

    loadPaystackScript();

    // Cleanup on unmount
    return () => {
      // Don't remove the script on unmount as it might be needed elsewhere
    };
  }, []);

  // Pre-fill user details if available
  useEffect(() => {
    if (user) {
      setUserDetails({
        email: user.email || '',
        firstName: user.name?.split(' ')[0] || '',
        lastName: user.name?.split(' ').slice(1).join(' ') || '',
        phone: user.phone || ''
      });
    }
  }, [user]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePaymentCallback = async (response) => {
    try {
      setLoading(true);
      // Verify payment with backend
      const result = await verifyPaystackPayment(response.reference, token);
      onSuccess(result);
    } catch (verifyError) {
      console.error('Payment verification failed:', verifyError);
      setError('Payment verification failed. Please contact support.');
      onError(verifyError);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!userDetails.email || !userDetails.firstName || !userDetails.lastName) {
      setError('Please fill in all required fields.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Initialize payment with backend
      const response = await initializePaystackPayment({
        ...plan,
        email: userDetails.email,
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        phone: userDetails.phone,
        userId: user?.id
      }, token);

      // Paystack initialization returns: { status: true, message: "...", data: { authorization_url: "...", access_code: "...", reference: "..." } }
      // Or sometimes just the data object depending on backend implementation. 
      // We'll handle both structures.
      const paymentData = response.data || response;

      console.log('Payment initialized:', paymentData);

      if (paymentData.authorization_url) {
        // PREFERRED METHOD: Redirect to Paystack secure checkout page
        // This avoids all X-Frame-Options and popup blocker issues
        window.location.href = paymentData.authorization_url;
        return;
      }

      // Fallback to popup if no authorization_url (should rarely happen for standard Paystack init)
      if (!isPaystackLoaded) {
        setError('Payment system loading. Please wait a moment and try again.');
        return;
      }

      // Configure Paystack popup as fallback
      const paystackConfig = {
        key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
        email: userDetails.email,
        amount: paymentData.amount, // Ensure amount is in kobo
        currency: 'NGN',
        ref: paymentData.reference,
        firstname: userDetails.firstName,
        lastname: userDetails.lastName,
        channels: ['card', 'bank', 'ussd', 'qr', 'mobile_money', 'bank_transfer'],
        metadata: {
          custom_fields: [
            {
              display_name: "Plan Name",
              variable_name: "plan_name",
              value: plan.name
            },
            {
              display_name: "Tokens",
              variable_name: "tokens",
              value: plan.tokens || 0
            }
          ]
        },
        callback: function (response) {
          handlePaymentCallback(response);
        },
        onClose: function () {
          setLoading(false);
          setError('Payment was cancelled.');
        }
      };

      const handler = window.PaystackPop.setup(paystackConfig);
      handler.openIframe();

    } catch (err) {
      console.error('Payment initialization error:', err);
      setError(err.message || 'An unexpected error occurred. Please try again.');
      onError(err);
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* User Details Form */}
      <div className="space-y-6">
        {/* Email Address */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Email Address
          </label>
          <div className="relative">
            <input
              type="email"
              name="email"
              value={userDetails.email}
              onChange={handleInputChange}
              required
              className="w-full p-4 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-gray-100 outline-none transition-all"
              placeholder="your@email.com"
            />
          </div>
        </div>

        {/* Name Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              First Name
            </label>
            <input
              type="text"
              name="firstName"
              value={userDetails.firstName}
              onChange={handleInputChange}
              required
              className="w-full p-4 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-gray-100 outline-none transition-all"
              placeholder="John"
            />
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Last Name
            </label>
            <input
              type="text"
              name="lastName"
              value={userDetails.lastName}
              onChange={handleInputChange}
              required
              className="w-full p-4 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-gray-100 outline-none transition-all"
              placeholder="Doe"
            />
          </div>
        </div>

        {/* Phone Number */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Phone Number (Optional)
          </label>
          <input
            type="tel"
            name="phone"
            value={userDetails.phone}
            onChange={handleInputChange}
            className="w-full p-4 border border-gray-200 rounded-xl bg-white shadow-sm focus:ring-2 focus:ring-gray-100 outline-none transition-all"
            placeholder="+234 123 456 7890"
          />
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-100 rounded-xl p-4">
          <p className="text-red-600 text-sm font-medium">{error}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={!isPaystackLoaded || loading}
        className="w-full bg-[#1a6b54] hover:bg-[#145341] text-white py-4 text-lg font-bold rounded-xl transition-all shadow-lg active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mt-4"
      >
        {loading ? (
          <div className="flex justify-center items-center gap-2">
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            <span>Processing...</span>
          </div>
        ) : !isPaystackLoaded ? (
          'Loading Payment System...'
        ) : (
          'Pay with Paystack'
        )}
      </button>

      <p className="text-xs text-gray-400 text-center leading-relaxed max-w-xs mx-auto">
        By confirming your purchase, you allow NKENNEAi to charge you for
        this payment in accordance with their terms. Payment is securely processed by Paystack.
      </p>
    </form>
  );
};

export default PaystackPaymentForm;
